import { Box, type SvgIconProps } from '@mui/material';
import {
  DataGridPremium,
  type DataGridPremiumProps,
  type GridColumnVisibilityModel,
  type GridDensity,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid-premium';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { COUNT_LIMIT } from '@shared/config';

const BackIcon = () => <ChevronLeft size={18} />;
const NextIcon = () => <ChevronRight size={18} />;
const SelectIcon = (props: SvgIconProps) => (
  <span
    className={props.className}
    style={{ display: 'flex', alignItems: 'center', pointerEvents: 'none' }}
  >
    <ChevronDown size={16} />
  </span>
);

function generateNestedOrderBy(
  field: string,
  order: string | null | undefined,
): any {
  if (!order) {
    return null;
  }
  const parts = field.split('.');
  if (parts.length === 1) {
    return { [field]: order + '_nulls_last' };
  }
  const [firstPart, ...rest] = parts;
  return { [firstPart]: generateNestedOrderBy(rest.join('.'), order) };
}

export function processMultipleOrderByFields(
  fields: string,
  order: string | null | undefined,
): any[] {
  return fields
    .split(',')
    .map(field => generateNestedOrderBy(field.trim(), order))
    .filter(Boolean);
}

const stringToDensity = (
  density: string | null | undefined,
  defaultDensity: GridDensity,
): GridDensity => {
  switch (density) {
    case 'compact':
      return 'compact';
    case 'standard':
      return 'standard';
    case 'comfortable':
      return 'comfortable';
    default:
      return defaultDensity;
  }
};

export type RaDataGridProps<R extends GridValidRowModel = any> =
  DataGridPremiumProps<R> & {
    queryParamsScope?: string;
    approximateRowCount?: boolean;
    // Page size used when the URL carries no ?limit — pass the SAME value to
    // the view's getPaginationVariables so the query and the pager agree.
    defaultPageSize?: number;
  };

export const RaDataGrid = <R extends GridValidRowModel = any>({
  sx,
  autoHeight,
  queryParamsScope,
  approximateRowCount = false,
  paginationMode = 'server',
  sortingMode = 'server',
  defaultPageSize,
  ...props
}: RaDataGridProps<R>) => {
  const [searchParams, setSearchParams] =
    useScopedSearchParams(queryParamsScope);
  const [originalSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const { limit, offset } = getPaginationVariables(
    searchParams,
    defaultPageSize,
  );

  const defaultVisibleColumns = props.columns.reduce((acc, column) => {
    return { ...acc, [column.field]: true };
  });

  const visible_columns: GridColumnVisibilityModel = JSON.parse(
    searchParams.get('visible_columns') ??
      JSON.stringify(defaultVisibleColumns),
  );

  const handeVisibleColumnsChange = (
    visible_columns: GridColumnVisibilityModel,
  ) => {
    searchParams.set('visible_columns', JSON.stringify(visible_columns));
    setSearchParams(searchParams);
  };
  const density: GridDensity = stringToDensity(
    searchParams.get('density'),
    props.density ?? 'standard',
  );

  const handleDensityChange = (density: GridDensity): void => {
    searchParams.set('density', density);
    setSearchParams(searchParams);
  };

  return (
    <Box
      sx={{
        flexGrow: autoHeight ? 0 : 1,
        position: 'relative',
      }}
    >
      <div
        style={{
          ...(autoHeight
            ? {}
            : { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }),
          backgroundColor: 'var(--mui-palette-background-paper)',
        }}
      >
        <DataGridPremium
          pagination
          paginationMode={paginationMode}
          paginationModel={
            paginationMode === 'server'
              ? {
                  pageSize: limit,
                  page: offset / limit,
                }
              : undefined
          }
          density={density}
          onDensityChange={handleDensityChange}
          pageSizeOptions={[25, 50, 100]}
          onPaginationModelChange={
            paginationMode === 'server'
              ? paginationModel => {
                  const { page, pageSize } = paginationModel;
                  searchParams.set('limit', pageSize.toString());
                  searchParams.set('offset', (page * pageSize).toString());
                  setSearchParams(searchParams);
                }
              : undefined
          }
          columnVisibilityModel={visible_columns}
          onColumnVisibilityModelChange={handeVisibleColumnsChange}
          sortingMode={sortingMode}
          disableColumnMenu={props.disableColumnMenu ?? true}
          disableColumnSelector={props.disableColumnSelector ?? true}
          onRowClick={row => {
            if (row.id === 'auto-generated-group-footer-root') {
              return;
            } else if (row.id != null) {
              navigate({
                pathname: `./${row.id}`,
                search: queryParamsScope
                  ? originalSearchParams.toString()
                  : searchParams.toString(),
              });
            }
          }}
          onSortModelChange={
            sortingMode === 'server'
              ? (sortModel: GridSortModel) => {
                  const order_by = sortModel.flatMap(({ field, sort }) =>
                    processMultipleOrderByFields(field, sort),
                  );

                  searchParams.set('order_by', JSON.stringify(order_by));
                  searchParams.set('offset', '0');
                  setSearchParams(searchParams);
                }
              : undefined
          }
          sx={{
            borderRadius: 0,
            // full-page grids sit flush against the sidebar, the chat panel
            // and the viewport bottom — drop those borders so they don't
            // double with the neighbours' own 1px borders; keep the top one
            // as the separator below the toolbar
            ...(autoHeight
              ? {}
              : {
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: 'none',
                }),
            // disable header selection style
            '.MuiDataGrid-columnHeader:focus': {
              outline: 'none',
            },
            // disable cell selection style
            '.MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            // pointer cursor on ALL rows
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
            ...(sx ?? {}),
          }}
          disableRowSelectionExcludeModel
          rowSelectionPropagation={{ parents: false, descendants: false }}
          autoHeight={autoHeight}
          {...props}
          rowCount={
            approximateRowCount
              ? props.rowCount === COUNT_LIMIT &&
                searchParams.get('show_real_count') !== 'true'
                ? -1
                : props.rowCount
              : props.rowCount
          }
          slotProps={{
            ...props.slotProps,
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
            pagination: {
              ...((props.slotProps?.pagination as Record<string, unknown>) ??
                {}),
              backIconButtonProps: {
                ...((
                  props.slotProps?.pagination as
                    | { backIconButtonProps?: object }
                    | undefined
                )?.backIconButtonProps ?? {}),
                children: <BackIcon />,
              },
              nextIconButtonProps: {
                ...((
                  props.slotProps?.pagination as
                    | { nextIconButtonProps?: object }
                    | undefined
                )?.nextIconButtonProps ?? {}),
                children: <NextIcon />,
              },
              SelectProps: {
                ...((
                  props.slotProps?.pagination as
                    | { SelectProps?: object }
                    | undefined
                )?.SelectProps ?? {}),
                IconComponent: SelectIcon,
              },
            },
          }}
        />
      </div>
    </Box>
  );
};
